import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ActivityProvider } from "./contexts/ActivityContext";
import { AskAiProvider } from "./contexts/AskAiContext";
import { AuthProvider } from "./contexts/AuthContext";
import { GroupProvider } from "./contexts/GroupContext";
import { PropertyProvider } from "./contexts/PropertyContext";
import { TaskProvider } from "./contexts/TaskContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import AskAiDrawer from "./components/AskAi/AskAiDrawer";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import SsoCallbackHandler from "./components/Auth/SsoCallbackHandler";
import RedirectIfAuthenticated from "./components/Common/RedirectIfAuthenticated";
import LoadingSpinner from "./components/Common/LoadingSpinner";
import ErrorBoundary from "./components/Common/ErrorBoundary";

// Route-level code splitting: each page becomes its own chunk instead of
// all of them (plus what they pull in — Leaflet via the properties map
// view, Recharts via the dashboard and expenses mini-dashboard) shipping
// in the single main bundle every visitor downloads on first load,
// regardless of which pages they ever open.
const LoginPage = lazy(() => import("./pages/LoginPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PropertiesPage = lazy(() => import("./pages/PropertiesPage"));
const PropertyDetailPage = lazy(() => import("./pages/PropertyDetailPage"));
const PropertyExpensesPage = lazy(() => import("./pages/PropertyExpensesPage"));
const PropertyFormPage = lazy(() => import("./pages/PropertyFormPage"));
const TasksPage = lazy(() => import("./pages/TasksPage"));
const CertificatesPage = lazy(() => import("./pages/CertificatesPage"));
const VaultContentsPage = lazy(() => import("./pages/VaultContentsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ExpensesPage = lazy(() => import("./pages/ExpensesPage"));
const ActivityPage = lazy(() => import("./pages/ActivityPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ExpenseImportPage = lazy(() => import("./pages/ExpenseImportPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <Router>
        <AuthProvider>
          <AskAiProvider>
          <TaskProvider>
            <ActivityProvider>
            <GroupProvider>
            <PropertyProvider>
              <AskAiDrawer />
              <SsoCallbackHandler />
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route
                  path="/login"
                  element={
                    <RedirectIfAuthenticated>
                      <LoginPage />
                    </RedirectIfAuthenticated>
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/properties"
                  element={
                    <ProtectedRoute>
                      <PropertiesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tasks"
                  element={
                    <ProtectedRoute>
                      <TasksPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/certificates"
                  element={
                    <ProtectedRoute>
                      <CertificatesPage />
                    </ProtectedRoute>
                  }
                />
                {/* Where a vault's wall QR points (VaultLocation.scan_url). */}
                <Route
                  path="/vaults/:vaultId"
                  element={
                    <ProtectedRoute>
                      <VaultContentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/expenses"
                  element={
                    <ProtectedRoute requiredPermission="expenseRecord">
                      <ExpensesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/activity"
                  element={
                    <ProtectedRoute>
                      <ActivityPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requiredRole="superadmin">
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/expense-import"
                  element={
                    <ProtectedRoute requiredPermission="expenseImport">
                      <ExpenseImportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/properties/new"
                  element={
                    <ProtectedRoute>
                      <PropertyFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/properties/:propertyId/edit"
                  element={
                    <ProtectedRoute>
                      <PropertyFormPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/properties/:propertyId/expenses"
                  element={
                    <ProtectedRoute requiredPermission="expenseRecord">
                      <PropertyExpensesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/properties/:propertyId"
                  element={
                    <ProtectedRoute>
                      <PropertyDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </Suspense>
            </PropertyProvider>
            </GroupProvider>
            </ActivityProvider>
          </TaskProvider>
          </AskAiProvider>
        </AuthProvider>
      </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
